<template>
  <router-link
    :to="routerLink"
    :class="$style.base"
  >
    <div
      :class="$style.section"
      v-test="'service-group-name'"
    >
      <BaseText
        :iconBefore="serviceGroup.services.length > 1 ? 'layers' : null"
      >
        {{ serviceGroup.name }}
      </BaseText>
    </div>
    <div :class="$style.section">
      <Flex
        v-if="company.medical && serviceGroup.services[0].medical"
        v-test="'service-group-icon-medical'"
      >
        <BaseIcon name="medical" />
      </Flex>
      <BaseText
        v-if="serviceGroup.services.length > 1"
        color="secondary"
        v-test="'service-group-variations-amount-text'"
      >
        {{ $screen === 's'
            ? serviceGroup.services.length +
              ' ' +
              $t('global.items.variation', serviceGroup.services.length)
            : $t('services.variations_amount', {
                amount: serviceGroup.services.length
              }) }}
      </BaseText>
      <div
        v-if="$screen !== 's'"
        :class="$style.duration"
        v-test="'service-group-duration'"
      >
        {{ `${duration} ${$t('global.items.minute', duration)}` }}
      </div>
      <div
        v-if="$screen !== 's'"
        :class="$style.price"
        v-test="'service-group-price'"
      >
        {{ filters.currency(price) }}
      </div>
      <div
        v-if="serviceCategorySortOder"
        :class="$style.sortOrder"
      >
        <BaseCounter
          v-model="sortOrderValue"
          :minValue="0"
          :maxValue="listLength - 1"
          hideValue
          reverse
          v-test="'service-group-sort-order'"
        />
      </div>
      <BaseMore
        v-else
        :options="['edit', 'duplicate', 'delete', 'sort']"
        @select="onMoreSelect"
        v-test="'service-group-more'"
      />
    </div>
  </router-link>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import filters from '@/filters';
import {
  isServicePackageDeletionError,
  servicePackageDeletionMessage,
} from '@/helpers/service-package';
import { flash, modal } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useServiceGroupsStore } from '@/stores/service-groups';

type Actions = 'edit' | 'duplicate' | 'delete' | 'sort';

export default defineComponent({
  props: {
    serviceGroup: {
      type: Object,
      required: true,
    },
    listLength: {
      type: Number,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
  },
  emits: ['updateSortOrder'],
  setup() {
    const { company } = useCompanyStore();
    const { t } = useI18n();
    const serviceCategorySortOder = inject('services-category-sort-order');
    const { deleteServiceVariationGroup, duplicateServiceVariationGroup } =
      useServiceGroupsStore();
    return {
      company,
      filters,
      serviceCategorySortOder,
      t,
      flash,
      deleteServiceVariationGroup,
      duplicateServiceVariationGroup,
    };
  },
  computed: {
    routerLink() {
      return {
        name: 'admin-service',
        params: {
          serviceId: this.serviceGroup.id,
        },
      };
    },
    sortOrderValue: {
      get() {
        return this.index;
      },
      set(value) {
        this.$emit('updateSortOrder', value);
      },
    },
    duration() {
      if (!this.serviceGroup?.services[0]?.duration) {
        return 0;
      }

      let minDuration = this.serviceGroup.services[0].duration;
      this.serviceGroup.services.forEach((variation) => {
        if (variation.duration < minDuration) {
          minDuration = variation.duration;
        }
      });
      return minDuration;
    },
    price() {
      if (!this.serviceGroup?.services[0]?.price) {
        return 0;
      }

      let minPrice = this.serviceGroup.services[0].price;
      this.serviceGroup.services.forEach((variation) => {
        if (variation.price < minPrice) {
          minPrice = variation.price;
        }
      });
      return minPrice;
    },
  },
  methods: {
    onMoreSelect(action: Actions) {
      switch (action) {
        case 'edit':
          this.$router.push(this.routerLink);
          break;

        case 'delete':
          modal('confirmation', {
            type: 'delete',
            item: this.serviceGroup.name,
          }).then(() => {
            this.deleteServiceVariationGroup(this.serviceGroup.id)
              .then(() => {
                this.flash(this.$t('global.flash.service_deleted'));
              })
              .catch((error) => {
                if (isServicePackageDeletionError(error?.graphQLErrors)) {
                  modal('warning', {
                    message: servicePackageDeletionMessage(
                      this.t,
                      this.serviceGroup.name,
                      error?.graphQLErrors,
                    ),
                  });
                }
              });
          });
          break;

        case 'duplicate':
          this.duplicateServiceVariationGroup(this.serviceGroup.id).then(() => {
            this.flash(this.$t('global.flash.service_duplicated'));
          });
          break;

        case 'sort':
          this.serviceCategorySortOder = true;
          break;

        default:
          break;
      }
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.section {
  display: flex;
  align-items: center;
  padding: $spacing;
  gap: $spacing;
}

.sortOrder {
  margin-top: $spacing * -0.5;
  margin-bottom: $spacing * -0.5;
}

.duration {
  white-space: nowrap;
}

.price {
  width: 57px;
  text-align: right;
}
</style>
